import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { toast } from 'react-toastify'

import CustomChip from '../../../../components/common/CustomChip.js'
import CustomSelectField from '../../../../components/common/CustomSelectField'
import CustomTable from '../../../../components/common/custom-table/CustomTable'
import CustomTabs from '../../../../components/common/CustomTabs.js'
import ConfirmDialog from '../../../../components/common/ConfirmDialog'
import { Icons, debounce, formatDate, formatVideoDuration } from '../../../../utils/utilities'
import api from '../../../../service/api.js'
import VideoPlayer from '../../../../components/common/video-player/VideoPlayer.js'
import { convertToCDNLink, isUrlAccessible } from '../../../../shared/utils/utilities.jsx'

export const RECORDING_STATUS_COLORS = { uploaded: "success", uploading: "secondary", failed: "error" }

const getErrorMessage = ( err, fallback ) => err?.response?.data?.message || fallback

const getInstituteCode = institute => typeof institute === "string" ? institute : institute?.institute_name_short

// Keeps the first occurrence of every subject_code
const uniqueSubjects = ( ...lists ) => {
    const seen = new Set()
    return lists.flat().filter( sub => sub?.subject_code && !seen.has( sub.subject_code ) && seen.add( sub.subject_code ) )
}

const DEFAULT_MODULES = [1, 2, 3, 4, 5].map( value => ( { label: `Module ${value}`, value } ) )

const labelSx = { fontSize: "0.875rem", display: "block", marginBottom: "4px" }

const Section = ( { label, children } ) => (
    <Box>
        <Typography fontSize="12px" fontWeight="600" color="textSecondary" marginBottom="8px">{label}</Typography>
        {children}
    </Box>
)

Section.propTypes = { label: PropTypes.node, children: PropTypes.node }

/**
 * Shows a recording with its signed video/thumbnail urls and lets the user edit or delete it.
 * Two columns (video | details) from md upwards, a single column below that with the video flush to the paper edges.
 *
 * @param {Object} props
 * @param {Object} props.recording - The row from the list. Shown straight away while the details load.
 * @param {Function} props.onClose - Called when the dialog should close.
 * @param {Function} [props.onDeleted] - Called with the recording id after a successful delete.
 * @param {Function} [props.onUpdated] - Called with the updated recording after a successful save.
 * @param {Array<{label: string, value: number}>} [props.modules] - Module options for the edit form.
 */
const RecordingPreviewDialog = ( { recording, onClose, onDeleted, onUpdated, modules = DEFAULT_MODULES } ) => {

    const [details, setDetails] = useState( null )
    const [loading, setLoading] = useState( true )
    const [loadError, setLoadError] = useState( null )
    const [videoDuration, setVideoDuration] = useState( null )
    const [confirmOpen, setConfirmOpen] = useState( false )
    const [deleting, setDeleting] = useState( false )

    const [activeTab, setActiveTab] = useState( "details" )
    const [assignments, setAssignments] = useState( null )
    const [loadingAssignments, setLoadingAssignments] = useState( false )
    const [assignmentsError, setAssignmentsError] = useState( null )

    const [isEditing, setIsEditing] = useState( false )
    const [editTitle, setEditTitle] = useState( "" )
    const [editTopics, setEditTopics] = useState( "" )
    const [editModule, setEditModule] = useState( "" )
    const [editModuleName, setEditModuleName] = useState( "" )
    const [editSubjects, setEditSubjects] = useState( [] )
    const [subjectOptions, setSubjectOptions] = useState( [] )
    const [searchingSubjects, setSearchingSubjects] = useState( false )
    const [saving, setSaving] = useState( false )

    const requestIdRef = useRef( 0 )
    const retriedExpiredUrlRef = useRef( false )
    const resumeAtRef = useRef( 0 )

    const theme = useTheme()
    const { palette, border, layout } = theme
    const isMobile = useMediaQuery( theme.breakpoints.down( "md" ) )

    const recordingId = recording.id
    // List data is used until the details arrive, so the dialog never opens empty
    const data = details || recording
    const subjects = data.subjects || []
    const topics = data.topics_covered || []
    const instituteCode = getInstituteCode( data.institute )
    const busy = deleting || saving

    // ---- Loading ------------------------------------------------------------

    const loadDetails = useCallback( async () => {
        const requestId = ++requestIdRef.current
        setLoading( true )
        setLoadError( null )
        try {
            const { data: { data: result } } = await api.onlineMeetings.fetchHybridClassRecordingDetails( recordingId )
            if ( requestId !== requestIdRef.current ) return // dialog closed or a newer request started

            const recordingCdnUrl = convertToCDNLink( result?.recording_metadata?.location )
            if ( await isUrlAccessible( recordingCdnUrl ) ) {
                result.recording_url = recordingCdnUrl
            }
            const thumbnailCdnUrl = convertToCDNLink( result?.thumbnail_metadata?.location )
            if ( await isUrlAccessible( thumbnailCdnUrl ) ) {
                result.thumbnail_url = thumbnailCdnUrl
            }

            setDetails( result )
        } catch ( err ) {
            if ( requestId !== requestIdRef.current ) return
            setLoadError( getErrorMessage( err, "Failed to load the recording." ) )
        } finally {
            if ( requestId === requestIdRef.current ) setLoading( false )
        }
    }, [recordingId] )

    useEffect( () => {
        loadDetails()
        // eslint-disable-next-line
        return () => { requestIdRef.current++ } // ignore any response that arrives after unmount
    }, [loadDetails] )

    // ---- Assignments ----------------------------------------------------------

    const loadAssignments = useCallback( async () => {
        setLoadingAssignments( true )
        setAssignmentsError( null )
        try {
            const { data: { data } } = await api.onlineMeetings.fetchRecordingAssignmentsDetails( recordingId )
            setAssignments( data || [] )
        } catch ( err ) {
            setAssignmentsError( getErrorMessage( err, "Failed to load the assignments." ) )
        } finally {
            setLoadingAssignments( false )
        }
    }, [recordingId] )

    // Fetched lazily, the first time the assignments tab is opened
    useEffect( () => {
        if ( activeTab === "assignments" && assignments === null ) loadAssignments()
    }, [activeTab, assignments, loadAssignments] )

    const assignmentColumns = useMemo( () => [
        {
            field: "institute", name: "Institute", width: 110,
            renderCell: row => <CustomChip value={row.time_table_class_details?.institute_name_short || "-"} />
        },
        {
            field: "class", name: "Class", width: 200,
            renderCell: row => {
                const t = row.time_table_class_details || {}
                return (
                    <Box>
                        <Typography fontSize="13px" fontWeight="600">{[t.course_short_name, t.course_branch_short_name].filter( Boolean ).join( " - " ) || "-"}</Typography>
                        <Typography fontSize="11.5px" color="textSecondary">{[t.batch_short_name, t.section_short_name && t.section_short_name !== "NA" ? t.section_short_name : null].filter( Boolean ).join( " · " )}</Typography>
                    </Box>
                )
            }
        },
        { field: "subject", name: "Subject", width: 100, renderCell: row => row.time_table_class_details?.subject_name_short || "-" },
        { field: "presenter", name: "Presenter", width: 160, renderCell: row => row.time_table_class_details?.presenter_name || "-" },
        {
            field: "scheduled", name: "Scheduled slot", width: 190,
            renderCell: row => {
                const t = row.time_table_class_details || {}
                return (
                    <Box>
                        <Typography fontSize="13px">{t.selected_date ? formatDate( t.selected_date ) : "-"}</Typography>
                        <Typography fontSize="11.5px" color="textSecondary">{t.intervals || "-"}</Typography>
                    </Box>
                )
            }
        },
        {
            field: "access_window", name: "Access window", width: 210,
            renderCell: row => <Typography fontSize="12.5px" color="textSecondary">{row.starts_at ? formatDate( row.starts_at, { showTime: true } ) : "-"} – {row.ends_at ? formatDate( row.ends_at, { showTime: true } ) : "-"}</Typography>
        },
        {
            field: "status", name: "Status",
            renderCell: row => {
                const status = row.time_table_class_details?.status
                if ( !status ) return "-"
                const color = status === "completed" ? "success" : status === "ongoing" ? "secondary" : "greyed"
                return <CustomChip value={status} variant="semifilled" color={color} />
            }
        }
    ], [] )

    // ---- Video --------------------------------------------------------------

    // Signed urls expire after a while, so a long viewing session can fail on seek.
    // Fetch fresh urls once and continue from the same position.
    const handleVideoError = ( event ) => {
        if ( retriedExpiredUrlRef.current ) {
            setLoadError( "The video could not be played." )
            return
        }
        retriedExpiredUrlRef.current = true
        resumeAtRef.current = event.target.currentTime || 0
        loadDetails()
    }

    const handleLoadedMetadata = ( e ) => {
        const video = e.currentTarget
        if ( Number.isFinite( video.duration ) ) setVideoDuration( video.duration )
        if ( resumeAtRef.current ) {
            video.currentTime = resumeAtRef.current
            resumeAtRef.current = 0
        }
    }

    const handleLoadedData = () => { retriedExpiredUrlRef.current = false }

    const handleRetry = () => {
        retriedExpiredUrlRef.current = false
        loadDetails()
    }

    const handleClose = () => {
        if ( busy ) return
        onClose()
    }

    // ---- Editing ------------------------------------------------------------

    // Selected subjects are always merged in, so a search never drops what is already picked
    const allSubjectOptions = useMemo( () => uniqueSubjects( editSubjects, subjectOptions ), [editSubjects, subjectOptions] )

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const searchSubjects = useCallback( debounce( "recording-preview-subject-search", async filter => {
        if ( !filter || filter.trim().length < 3 ) {
            setSubjectOptions( [] )
            return
        }
        setSearchingSubjects( true )
        try {
            const { data: { data: fetched } } = await api.audit.fetchSubjects( { subject_code: filter.trim(), institute: instituteCode } )
            setSubjectOptions( fetched || [] )
        } catch ( err ) {
            toast.error( getErrorMessage( err, `Failed to fetch subjects for '${filter}'.` ) )
        } finally {
            setSearchingSubjects( false )
        }
    }, 500 ), [instituteCode] )

    const handleSubjectChange = ( event ) => {
        const codes = event.target.value || []
        setEditSubjects( codes.map( code => allSubjectOptions.find( sub => sub.subject_code === code ) ).filter( Boolean ) )
    }

    const renderSelectedSubjects = ( codes = [] ) => {
        if ( codes.length === 0 ) return "Select subjects"
        const [first, ...rest] = codes.map( code => allSubjectOptions.find( sub => sub.subject_code === code ) || { subject_code: code } )
        return (
            <Box display="flex" gap={0.5} flexWrap="wrap">
                <CustomChip value={first.subject_code} tooltipTitle={first.subject_name} noWrap textStyles={{ fontSize: "0.75rem" }} />
                {rest.length > 0 && (
                    <CustomChip
                        value={`+${rest.length} more`}
                        noWrap
                        textStyles={{ fontSize: "0.75rem" }}
                        tooltipTitle={<Box>
                            {rest.map( ( sub, idx ) => (
                                <Typography key={sub.subject_code} component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 500 }}>
                                    {`${idx + 2}. ${sub.subject_name || sub.subject_code}`}
                                </Typography>
                            ) )}
                        </Box>}
                    />
                )}
            </Box>
        )
    }

    const handleStartEdit = () => {
        setEditTitle( data.title || "" )
        setEditTopics( topics.join( ", " ) )
        setEditModule( data.module_number ?? "" )
        setEditModuleName( data.module_name || "" )
        setEditSubjects( subjects )
        setSubjectOptions( [] )
        setIsEditing( true )
    }

    const handleCancelEdit = () => setIsEditing( false )

    const handleSaveEdit = async () => {
        const title = editTitle.trim()
        if ( !title ) {
            toast.error( "Title is required." )
            return
        }
        if ( editSubjects.length === 0 ) {
            toast.error( "Select at least one subject." )
            return
        }
        if ( editModule === "" || editModule == null ) {
            toast.error( "Select a module." )
            return
        }
        const module_number = Number( editModule )
        const module_name = editModuleName.trim()
        const topics_covered = editTopics.split( "," ).map( topic => topic.trim() ).filter( Boolean )

        setSaving( true )
        try {
            await api.onlineMeetings.updateHybridClassVideo( recordingId, { title, module_number, module_name, topics_covered, subjects: editSubjects } )
            const updated = { ...data, title, module_number, module_name, topics_covered, subject_codes: editSubjects.map( sub => sub.subject_code ), subjects: editSubjects }
            setDetails( updated )
            onUpdated?.( updated )
            toast.success( "Recording updated." )
            setIsEditing( false )
        } catch ( err ) {
            toast.error( getErrorMessage( err, "Failed to update the recording." ) )
        } finally {
            setSaving( false )
        }
    }

    // ---- Deleting -----------------------------------------------------------

    const handleConfirmDelete = async () => {
        setDeleting( true )
        try {
            await api.onlineMeetings.deleteHybridClassRecording( recordingId )
            toast.success( "Recording deleted." )
            setConfirmOpen( false )
            onDeleted?.( recordingId )
            onClose()
        } catch ( err ) {
            toast.error( getErrorMessage( err, "Failed to delete the recording." ) )
        } finally {
            setDeleting( false )
        }
    }

    const durationSeconds = videoDuration ?? data.recording_metadata?.duration_seconds ?? 0
    const statusColor = RECORDING_STATUS_COLORS[data.status]
    const hasVideo = Boolean( details?.recording_url )

    return (
        <Dialog
            open
            maxWidth={false}
            slotProps={{
                paper: {
                    sx: {
                        position: "relative",
                        width: { xs: "calc(100% - 24px)", md: "min(1040px, calc(100% - 64px))" },
                        maxWidth: "none",
                        maxHeight: { xs: "calc(100% - 24px)", md: "min(620px, calc(100% - 64px))" },
                        margin: { xs: "12px", md: "32px" },
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }
                }
            }}
        >
            <IconButton
                aria-label="Close"
                size="small"
                onClick={handleClose}
                disabled={busy}
                sx={{
                    position: "absolute", top: 10, right: 10, zIndex: 2,
                    // On mobile it floats over the video, so it needs its own backdrop
                    ...( isMobile && { bgcolor: "rgba(0, 0, 0, 0.5)", color: "#fff", "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" } } ),
                }}
            >
                {Icons.small.CloseIcon}
            </IconButton>

            <Box
                flex={1}
                minHeight={0}
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                sx={{ overflow: "hidden" }}
            >
                {/* ---- Video column ---- */}
                <Box
                    display="flex"
                    alignItems="center"
                    flex={{ xs: "0 0 auto", md: "1.6 1 0%" }}
                    minWidth={{ md: 0 }}
                    padding={{ xs: 0, md: "20px" }}
                    bgcolor={{ md: palette.contentBg }}
                    borderRight={{ md: border[1] }}
                >
                    <Box
                        position="relative"
                        width="100%"
                        maxHeight={{ md: "initial", xs: "320px" }}
                        sx={{ aspectRatio: "16 / 9" }}
                        borderRadius={{ xs: 0, md: layout.borderRadius }}
                        overflow="hidden"
                        bgcolor={palette.contentBg}
                        color="#fff"
                    >
                        {hasVideo && (
                            <VideoPlayer
                                containerHeight="100%"
                                key={details.recording_url}
                                src={details.recording_url}
                                poster={details.thumbnail_url || undefined}
                                controls
                                preload="metadata"
                                playsInline
                                onError={handleVideoError}
                                onLoadedMetadata={handleLoadedMetadata}
                                onLoadedData={handleLoadedData}
                            />
                        )}

                        {( loading || loadError || !hasVideo ) && (
                            <Box
                                position="absolute" sx={{ inset: 0 }}
                                display="flex" flexDirection="column" gap="12px" alignItems="center" justifyContent="center"
                                textAlign="center" padding="16px"
                                bgcolor={hasVideo ? "rgba(0, 0, 0, 0.55)" : "transparent"}
                            >
                                {loading
                                    ? <CircularProgress size={28} color="inherit" />
                                    : <>
                                        <Typography fontSize="13px">{loadError || "No video is available for this recording."}</Typography>
                                        {loadError && <Button size="small" variant="outlined" color="inherit" onClick={handleRetry} sx={{ textTransform: "capitalize" }}>Try again</Button>}
                                    </>
                                }
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ---- Details column ---- */}
                <Box display="flex" flexDirection="column" minHeight={0} flex={{ xs: "1 1 auto", md: "1 1 320px" }} minWidth={{ md: "320px" }}>
                    {!isEditing && (
                        <Box padding="14px 20px 0">
                            <CustomTabs
                                value={activeTab}
                                handleTabChange={setActiveTab}
                                tabs={[{ value: "details", label: "Details" }, { value: "assignments", label: "Assignments" }]}
                                variant="stretched"
                                styles={{ border: border[1], borderRadius: "6px", overflow: "hidden" }}
                            />
                        </Box>
                    )}

                    {( activeTab === "details" || isEditing ) ? (
                        <Box flex={1} minHeight={0} sx={{ overflowY: "auto" }} padding="20px" display="flex" flexDirection="column" gap="20px">

                            <Box>
                                <Box display="flex" flexWrap="wrap" gap="6px" paddingRight={{ md: "36px" }} marginBottom="10px">
                                    {data.status && (
                                        <CustomChip
                                            value={data.status}
                                            color={statusColor}
                                            variant="semifilled"
                                            startIcon={<Box component="span" sx={{ width: "6px", height: "6px", borderRadius: "50%", bgcolor: statusColor ? `${statusColor}.main` : "text.secondary" }} />}
                                        />
                                    )}
                                    {data.module_number != null && <CustomChip value={`Module ${data?.module_number}${data?.module_name ? `: ${data?.module_name}` : ""}`} />}
                                    <CustomChip value={formatVideoDuration( durationSeconds )} startIcon={Icons.small.Clock} />
                                </Box>

                                {isEditing ? (
                                    <Box>
                                        <Typography component="label" htmlFor="recording-title" sx={labelSx}>Video title *</Typography>
                                        <TextField
                                            id="recording-title"
                                            autoFocus
                                            size="small"
                                            fullWidth
                                            value={editTitle}
                                            onChange={e => setEditTitle( e.target.value )}
                                            placeholder="Eg: Linked lists part 1"
                                            disabled={saving}
                                        />
                                    </Box>
                                ) : (
                                    <>
                                        <Typography component="h2" fontSize="18px" fontWeight="600" lineHeight={1.35} sx={{ wordBreak: "break-word" }}>
                                            {data.title}
                                        </Typography>
                                        {data.module_name && <Typography fontSize="13px" color="textSecondary" marginTop="4px">{data.module_name}</Typography>}
                                    </>
                                )}
                            </Box>

                            {isEditing ? (
                                <Box>
                                    <Typography component="label" htmlFor="recording-subjects" sx={labelSx}>Subjects *</Typography>
                                    <CustomSelectField
                                        id="recording-subjects"
                                        multiple
                                        size="small"
                                        fullWidth
                                        disabled={saving}
                                        value={editSubjects.map( sub => sub.subject_code )}
                                        data={allSubjectOptions}
                                        itemValueKey="subject_code"
                                        onChange={handleSubjectChange}
                                        onSearch={searchSubjects}
                                        minSearchLength={3}
                                        loadingOptions={searchingSubjects}
                                        searchPlaceholder="Search by subject code..."
                                        loadinOptionsMessage="Searching subjects..."
                                        renderValue={renderSelectedSubjects}
                                        renderOptionText={sub => (
                                            <Box>
                                                <Typography sx={{ fontSize: "14px", textTransform: "capitalize" }}>{sub.subject_name?.toLowerCase()}</Typography>
                                                <CustomChip value={sub.subject_code} />
                                            </Box>
                                        )}
                                        sx={{ overflow: "hidden" }}
                                    />
                                </Box>
                            ) : (
                                <Section label={subjects.length === 1 ? "SUBJECT" : "SUBJECTS"}>
                                    {subjects.length > 0 ? (
                                        <Box display="flex" flexDirection="column" gap="8px">
                                            {subjects.map( sub => (
                                                <Box key={sub.subject_code} display="flex" alignItems="center" gap="10px" minWidth={0}>
                                                    <CustomChip value={sub.subject_code} color="secondary" variant="semifilled" />
                                                    <Typography fontSize="13px" fontWeight="500" noWrap title={sub.subject_name} sx={{ textTransform: "capitalize" }}>
                                                        {sub.subject_name?.toLowerCase()}
                                                    </Typography>
                                                </Box>
                                            ) )}
                                        </Box>
                                    ) : (
                                        <Typography fontSize="13px" color="textSecondary">No subjects tagged yet.</Typography>
                                    )}
                                </Section>
                            )}

                            {isEditing && (
                                <Box display="grid" gridTemplateColumns="minmax(120px, 0.8fr) minmax(0, 1.2fr)" gap="12px">
                                    <Box display="flex" flexDirection="column">
                                        <Typography component="label" htmlFor="recording-module" sx={labelSx}>Module *</Typography>
                                        <CustomSelectField
                                            id="recording-module"
                                            size="small"
                                            fullWidth
                                            disabled={saving}
                                            value={editModule}
                                            onChange={e => setEditModule( e.target.value )}
                                            data={modules}
                                            itemValueKey="value"
                                            placeholder="Module"
                                        />
                                    </Box>
                                    <Box display="flex" flexDirection="column">
                                        <Typography component="label" htmlFor="recording-module-name" sx={labelSx}>Module name</Typography>
                                        <TextField
                                            id="recording-module-name"
                                            size="small"
                                            fullWidth
                                            value={editModuleName}
                                            onChange={e => setEditModuleName( e.target.value )}
                                            placeholder="Eg: Linked lists"
                                            disabled={saving}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {isEditing ? (
                                <Box>
                                    <Typography component="label" htmlFor="recording-topics" sx={labelSx}>Topics covered *</Typography>
                                    <TextField
                                        id="recording-topics"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        value={editTopics}
                                        onChange={e => setEditTopics( e.target.value )}
                                        placeholder="Eg: Singly linked list, Doubly linked list"
                                        disabled={saving}
                                    />
                                    <Typography variant="body2" component="em" sx={{ fontSize: "12px", color: "text.secondary" }}>Separate topics with commas.</Typography>
                                </Box>
                            ) : (
                                <Section label="TOPICS COVERED">
                                    {topics.length > 0 ? (
                                        <Box display="flex" flexWrap="wrap" gap="6px">
                                            {topics.map( ( topic, idx ) => <CustomChip key={`${topic}-${idx}`} value={topic} /> )}
                                        </Box>
                                    ) : (
                                        <Typography fontSize="13px" color="textSecondary">No topics added yet.</Typography>
                                    )}
                                </Section>
                            )}

                            {( data.recording_metadata?.original_name || data.uploaded_at ) && (
                                <Box paddingTop="16px" borderTop={`1px dashed ${palette.borderColor}`}>
                                    {data.recording_metadata?.original_name && (
                                        <Typography fontSize="12px" color="textSecondary" sx={{ wordBreak: "break-all" }}>{data.recording_metadata.original_name}</Typography>
                                    )}
                                    {data.uploaded_at && (
                                        <Typography fontSize="12px" color="textSecondary">Uploaded {formatDate( data.uploaded_at, { showTime: true } )}</Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box flex={1} minHeight={0} sx={{ overflowY: "auto" }} padding="20px" display="flex" flexDirection="column" gap="12px">
                            {loadingAssignments && (
                                <Box display="flex" alignItems="center" justifyContent="center" padding="30px">
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            {!loadingAssignments && assignmentsError && (
                                <Box display="flex" flexDirection="column" gap="10px" alignItems="center" padding="20px" textAlign="center">
                                    <Typography fontSize="13px" color="textSecondary">{assignmentsError}</Typography>
                                    <Button size="small" variant="outlined" onClick={loadAssignments} sx={{ textTransform: "capitalize" }}>Try again</Button>
                                </Box>
                            )}
                            {!loadingAssignments && !assignmentsError && (
                                <CustomTable
                                    tableKey="recording-assignments"
                                    columns={assignmentColumns}
                                    data={assignments || []}
                                    pageSize={50}
                                    containerStyles={{ maxHeight: 360 }}
                                    noDataBlock={<Typography fontSize="13px" color="textSecondary">This recording hasn't been assigned to any class yet.</Typography>}
                                />
                            )}
                        </Box>
                    )}

                    {/* ---- Actions ---- */}
                    {activeTab !== "assignments" && <Box
                        display="flex" justifyContent="space-between" alignItems="center" gap="8px"
                        padding="10px 20px"
                        borderTop={border[1]}
                        bgcolor={palette.background.paper}
                    >
                        {isEditing ? (
                            <>
                                <Button size="small" color="greyed" disabled={saving} onClick={handleCancelEdit} sx={{ textTransform: "capitalize" }}>Cancel</Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    disabled={saving}
                                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                                    onClick={handleSaveEdit}
                                    sx={{ textTransform: "capitalize" }}
                                >
                                    Save changes
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    color="error"
                                    size="small"
                                    startIcon={Icons.small.DeleteIcon}
                                    disabled={deleting}
                                    onClick={() => setConfirmOpen( true )}
                                    sx={{ textTransform: "capitalize" }}
                                >
                                    Delete
                                </Button>
                                <Button
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                    startIcon={Icons.small.EditIcon}
                                    disabled={loading || !!loadError}
                                    onClick={handleStartEdit}
                                    sx={{ textTransform: "capitalize" }}
                                >
                                    Edit details
                                </Button>
                            </>
                        )}
                    </Box>}
                </Box>
            </Box>

            <ConfirmDialog
                status={confirmOpen}
                waiting={deleting}
                message={<span>The recording <strong>{data.title}</strong> and its video files will be deleted permanently.</span>}
                actionName="Delete recording"
                confirmAction={handleConfirmDelete}
                cancelAction={() => { if ( !deleting ) setConfirmOpen( false ) }}
            />
        </Dialog>
    )
}

RecordingPreviewDialog.propTypes = {
    recording: PropTypes.shape( {
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        subject_codes: PropTypes.arrayOf( PropTypes.string ),
        subjects: PropTypes.arrayOf( PropTypes.shape( {
            subject_code: PropTypes.string,
            subject_name: PropTypes.string,
        } ) ),
    } ).isRequired,
    onClose: PropTypes.func.isRequired,
    onDeleted: PropTypes.func,
    onUpdated: PropTypes.func,
    modules: PropTypes.arrayOf( PropTypes.shape( {
        label: PropTypes.string,
        value: PropTypes.oneOfType( [PropTypes.number, PropTypes.string] ),
    } ) ),
}

export default RecordingPreviewDialog